import React, { useCallback, useEffect, useState } from "react";

import { sdkInstance as sdk } from "./MP_NPS_CSAT/Instance";
import { Plugin } from "./MP_NPS_CSAT/Plugin";
import { useServerSettings } from "./hooks/useServerSettings";
import { csatRatings, getColorStyle } from "./styleUtils";

import "./styles.css";

export type Feedback = {
  csat?: number;
  nps?: number;
  feedback: string;
  user: string;
  submitDate: string;
};

const Popup = () => {
  const serverSettings = useServerSettings();
  const [showPopup, setShowPopup] = useState(false);
  const [csatRating, setCsatRating] = useState<number>(null);
  const [npsRating, setNpsRating] = useState<number>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [allFeedback, setAllFeedback] = useState<Feedback[]>([]);
  const [daysSinceLastFeedback, setDaysSinceLastFeedback] = useState(0);

  const matrixSession = sdk.matrixSession;
  const config = new Plugin().getConfig();

  const fetchUserFeedback = useCallback(async (): Promise<{
    feedbacks: Feedback[];
  }> => {
    const currentUserSettings =
      matrixSession?.serverConfig?.currentUserSettings;
    const projectSettingName = config.customerSettingsPage.settingName;
    const feedbackSetting = currentUserSettings?.find(
      (s) => s.key === projectSettingName,
    );

    if (feedbackSetting) {
      return JSON.parse(feedbackSetting.value);
    } else {
      return { feedbacks: [] };
    }
  }, [matrixSession, config]);

  const getDaysSinceLastFeedback = (feedbacks: Feedback[]) => {
    let lastFeedbackDate = moment(new Date(0));

    for (const feedback of feedbacks) {
      const feedbackMoment = moment(feedback.submitDate);
      if (feedbackMoment > lastFeedbackDate) {
        lastFeedbackDate = feedbackMoment;
      }
    }
    setDaysSinceLastFeedback(moment().diff(lastFeedbackDate, "days"));
  };

  useEffect(() => {
    fetchUserFeedback()
      .then((feedbackData) => {
        setAllFeedback(feedbackData.feedbacks);
      })
      .catch((error) => console.error("Error fetching user feedback:", error));
  }, [fetchUserFeedback]);

  useEffect(() => {
    if (allFeedback.length > 0) {
      getDaysSinceLastFeedback(allFeedback);
    }
  }, [allFeedback]);

  useEffect(() => {
    const decideToShowPopup = () => {
      if (daysSinceLastFeedback > serverSettings?.daysBeetweenFeedbacks) {
        const closedKey = "csat_popup_closed_" + matrixSession.getUser();
        const closed = localStorage.getItem(closedKey);
        if (closed) {
          const closedDate = new Date(closed);
          const daysSinceClosed = moment().diff(closedDate, "days");
          if (daysSinceClosed > serverSettings?.daysAfterClosedPopup) {
            return serverSettings?.timeOutBeforePopup;
          }
        } else {
          return serverSettings?.timeOutBeforePopup;
        }
      }
      return null;
    };

    const delay = decideToShowPopup();

    if (delay !== null) {
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, delay);

      return () => clearTimeout(timer);
    } else {
      setShowPopup(false);
    }
  }, [serverSettings, daysSinceLastFeedback, matrixSession]);

  const handleRatingClick = (ratingType: "csat" | "nps", value: number) => {
    if (ratingType === "csat") {
      setCsatRating(value);
    } else {
      setNpsRating(value);
    }
  };

  const handleFeedbackTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setFeedbackText(e.target.value);
  };

  const closePopup = () => {
    setShowPopup(false);
    localStorage.setItem(
      "csat_popup_closed_" + matrixSession.getUser(),
      new Date().toISOString(),
    );
  };

  const handleSubmit = async () => {
    const config = new Plugin().getConfig();
    const settingId = config.customerSettingsPage.settingName;
    const matrixSession = sdk.matrixSession;
    const userFeedback = await fetchUserFeedback();
    const data = {
      csat: csatRating,
      nps: npsRating,
      feedback: feedbackText,
      user: matrixSession.getUser(),
      submitDate: new Date().toISOString(),
    };

    await restConnection.postServer(
      "user/" + matrixSession.getUser() + "/setting",
      {
        key: settingId,
        value: JSON.stringify({ feedbacks: [...userFeedback.feedbacks, data] }),
        reason: "Respond to CSAT",
      },
    );

    setShowPopup(false);
    localStorage.setItem(
      "csat_popup_closed_" + matrixSession.getUser(),
      new Date().toISOString(),
    );
    ml.UI.showSuccess("Thank you for your feedback!");
  };

  if (!showPopup) {
    return null;
  }

  return (
    <div
      className={`csat_popup ${showPopup ? "fadeIn" : ""}`}
      style={{ display: showPopup ? "block" : "none" }}
    >
      <div className="csat_header_close" onClick={closePopup}>
        <div className="csat_header_close_icon fal fa-times-circle" />
      </div>

      <div className="csat_body">
        <div className="csat_header_title">
          <div className="csat_header_title_text">
            How satisfied are you with Matrix Requirements?
          </div>
        </div>
        <div className="csat_body_ratings">
          {csatRatings.map((rating, i) => (
            <span
              key={rating.value}
              className={`${rating.className} csat rating ${
                csatRating === i + 1 ? "selected" : ""
              }`}
              data-value={rating.value}
              onClick={() => handleRatingClick("csat", rating.value)}
            />
          ))}
        </div>

        <div className="csat_header_title">
          How likely would you recommend our solutions to a friend or a
          co-worker?
        </div>
        <div>
          <div className="csat_body_ratings">
            {Array.from({ length: 11 }, (_, i) => (
              <span
                key={i}
                className={`nps rating ${getColorStyle(i)} ${
                  npsRating === i ? "selected" : ""
                }`}
                data-value={i}
                onClick={() => handleRatingClick("nps", i)}
              >
                {i}
              </span>
            ))}
          </div>

          <div className="csat_body_ratings_likely_unlikely">
            <div className="likely">very likely</div>
            <div className="unlikely">not likely</div>
          </div>
        </div>
        <div className="feedback_container">
          <textarea
            rows={3}
            className="feedbackText"
            placeholder="What can we do to make your experience more satisfying?"
            onChange={handleFeedbackTextChange}
          />
        </div>
      </div>

      <div className="csat_footer">
        <div className="feedback_info">
          Your feedback is important to us. Please take a moment to rate your
          experience.
        </div>
        <button
          disabled={csatRating == null || npsRating == null}
          className="btn btn-success csat_submit"
          onClick={handleSubmit}
        >
          Send feedback
        </button>
      </div>
    </div>
  );
};

export default Popup;
